import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Activity, MessageSquarePlus } from "lucide-react";
import EmptyState from "@/components/EmptyState";
import CommentThread from "@/components/CommentThread";
import { fmtRelative } from "@/lib/format";

/**
 * <ActivityTab entityType entityId>
 *   - Top: composer that posts a manual thread comment scoped to this entity.
 *   - Below: a merged timeline of:
 *       1. activity_log rows (system changelog) — from /activity/{entity_type}/{entity_id}
 *       2. thread_comments — from /threads/{entity_type}/{entity_id}/comments
 *
 * Posting a comment via CommentThread refreshes the thread part automatically.
 */
export default function ActivityTab({ entityType, entityId, testid = "activity-tab" }) {
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(true);

  const loadLogs = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/activity/${entityType}/${entityId}`);
      setLogs(data || []);
    } finally { setBusy(false); }
  };
  useEffect(() => { if (entityId) loadLogs(); }, [entityType, entityId]);

  // Render system log items in a clean timeline
  return (
    <div className="space-y-6" data-testid={testid}>
      <section data-testid={`${testid}-thread-wrapper`}>
        <div className="label-eyebrow text-[10px] inline-flex items-center gap-2 mb-2">
          <MessageSquarePlus className="w-3 h-3" /> Manual comments
        </div>
        <CommentThread
          entityType={entityType}
          entityId={entityId}
          title=""
          emptyHint="Add the first comment on this record. Mentions, decisions, account notes — all live here."
          testid={`${testid}-thread`}
        />
      </section>

      <section data-testid={`${testid}-changelog`}>
        <div className="label-eyebrow text-[10px] inline-flex items-center gap-2 mb-2">
          <Activity className="w-3 h-3" /> Activity log · {logs.length}
        </div>
        {busy && logs.length === 0 ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : logs.length === 0 ? (
          <EmptyState
            title="No changes yet"
            description="Every system change (status updates, field edits, attachments, role assignments) will show here."
            icon={Activity}
            testid={`${testid}-empty`}
          />
        ) : (
          <ul className="border border-border divide-y divide-border" data-testid={`${testid}-list`}>
            {logs.map((l) => (
              <li key={l.id} className="px-4 py-2 grid grid-cols-12 gap-2 items-center text-sm" data-testid={`${testid}-row-${l.id}`}>
                <div className="col-span-3 text-xs">
                  <div className="font-medium truncate">{l.actor_name || "—"}</div>
                  <div className="text-[10px] text-muted-foreground">{fmtRelative(l.created_at)}</div>
                </div>
                <div className="col-span-9 min-w-0">
                  <div className="truncate">{describeLog(l)}</div>
                  {l.metadata && Object.keys(l.metadata || {}).length > 0 && (
                    <div className="text-[11px] text-muted-foreground truncate">
                      {Object.entries(l.metadata).slice(0, 3).map(([k, v]) => `${k}: ${String(v).slice(0, 80)}`).join(" · ")}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function describeLog(l) {
  if (l.summary) return l.summary;
  const act = (l.action || "updated").replace(/_/g, " ");
  return `${act} ${l.target_type || ""}`.trim();
}
